import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import TopProgressBar from '../components/common/TopProgressBar';
import SignupStep from '../components/steps/SignupStep';
import BankAccountStep from '../components/steps/BankAccountStep';
import VerificationStep from '../components/steps/VerificationStep';
import { SignupFooter } from '../components/common';
import './GetStartedScreen.css';

export interface GetStartedScreenProps {
  initialStep?: number;
}

const  GetStartedScreen: React.FC<GetStartedScreenProps> = ({ initialStep = 1 }) => {
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [accountsPersisted, setAccountsPersisted] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Protect /verification route - prevent direct access
  useEffect(() => {
    // Check if user is trying to access /verification directly (via URL)
    if (location.pathname === '/verification') {
      // Check if user has completed the flow using internal state
      if (!accountsPersisted) {
        // User hasn't completed the flow, redirect to bank account step
        console.log('Direct access to /verification detected without completing flow, redirecting to /linkBankAccount');
        navigate('/linkBankAccount', { replace: true });
        setCurrentStep(2);
        return;
      }
      // If user has completed flow and is on /verification, ensure step is set to 3
      if (currentStep !== 3 && accountsPersisted) {
        setCurrentStep(3);
      }
    }
  }, [location.pathname, navigate, accountsPersisted, currentStep]);

  // Handle redirect from Finicity - if we're on /verification with redirect params, go to /linkBankAccount
  useEffect(() => {
    const reason = searchParams.get('reason');
    const code = searchParams.get('code');
    
    if (location.pathname === '/verification' && (reason || code)) {
      // Finicity redirected to /verification, but we need to show AccountSelectionStep in /linkBankAccount
      console.log('Finicity redirect detected on /verification, redirecting to /linkBankAccount');
      navigate(`/linkBankAccount?reason=${reason || ''}&code=${code || ''}`, { replace: true });
      setCurrentStep(2); // Show BankAccountStep which will handle the redirect
      return;
    }
  }, [location.pathname, searchParams, navigate]);

  // Update URL when step changes (but not if we're handling a redirect)
  useEffect(() => {
    const reason = searchParams.get('reason');
    const code = searchParams.get('code');
    
    // Don't update URL if we're handling a redirect
    if (reason || code) {
      return;
    }

    const stepToUrl = {
      1: '/signup',
      2: '/linkBankAccount',
      3: '/verification'
    };

    const expectedUrl = stepToUrl[currentStep as keyof typeof stepToUrl];
    
    // When moving to step 3 (verification), always update URL to /verification
    if (currentStep === 3 && location.pathname !== '/verification') {
      navigate('/verification', { replace: true });
    } else if (expectedUrl && location.pathname !== expectedUrl && currentStep !== 3) {
      navigate(expectedUrl, { replace: true });
    }
  }, [currentStep, navigate, location.pathname, searchParams]);

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleAccountsPersisted = () => {
    setAccountsPersisted(true);
    handleNextStep();
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <SignupStep onNext={handleNextStep} />;
      case 2:
        return <BankAccountStep onNext={handleAccountsPersisted} onPrev={handlePrevStep} />;
      case 3:
        return <VerificationStep onNext={handleNextStep} onPrev={handlePrevStep} />;
      default:
        return <SignupStep onNext={handleNextStep} />;
    }
  };

  return (
    <div>
    <div className="get-started-screen">
      {/* Fixed Top Header Bar */}
      <TopProgressBar />

      {/* Content Area with Progress Indicator */}
      <div className="content-area">
        <div className="step-content">

          {/* Step Content */}
          <div className="step-content-wrapper">
            {renderStepContent()}
          </div>
        </div>
      </div>

      {/* Footer */}
     
    </div>
     <SignupFooter />
     </div>
  );
};

export default GetStartedScreen; 